import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../lib/auth";
import DemoRequestDialog from "../components/DemoRequestDialog";
import {
  ArrowRight, ShieldCheck, CheckCircle, XCircle, Buildings, Users, Bank,
  Leaf, MapTrifold, Briefcase, ListChecks, Calendar, ChartLine, Bell,
  FileArrowDown, Handshake, Target, Scales, Compass, Mountains,
  Broadcast, Database, Clock, Sparkle, Lightning,
} from "@phosphor-icons/react";

// --------------------------------------------------------------------------
// SIGSAOP — Commercial landing page
// --------------------------------------------------------------------------

const MODULES = [
  { icon: Users, title: "Comunidades", desc: "Ficha completa, líderes, ubicación, historial de reuniones y proyectos." },
  { icon: Bank, title: "Autoridades", desc: "Comunales, gobierno, parlamentarios, GORE y organizaciones." },
  { icon: ListChecks, title: "Compromisos", desc: "RCA, voluntarios y contractuales, con semáforo y vencimientos." },
  { icon: Briefcase, title: "Proyectos", desc: "Inversión social y ambiental con beneficiarios y avance %." },
  { icon: Leaf, title: "RCA", desc: "Resoluciones de Calificación Ambiental con permisos y monitoreo." },
  { icon: Calendar, title: "Reuniones", desc: "Agenda, acuerdos, participantes y registro detallado." },
  { icon: Target, title: "Metas / Asuntos públicos", desc: "Ejes estratégicos con metas medibles y hitos anuales." },
  { icon: MapTrifold, title: "Mapa geográfico", desc: "Visualización territorial de comunidades, proyectos y autoridades." },
  { icon: Bell, title: "Alertas y notificaciones", desc: "Correos automáticos 30/15/7/3/1 día antes del vencimiento." },
  { icon: FileArrowDown, title: "Reportes y exportables", desc: "PDF y Excel de todo lo gestionado, listo para directorio." },
];

const VALUES = [
  { icon: Database, title: "Un solo repositorio", desc: "Fin del Excel disperso. Un lugar canónico con trazabilidad completa." },
  { icon: Broadcast, title: "Multi-tenant real", desc: "Cada organización con su data aislada. Sin fugas entre clientes." },
  { icon: ShieldCheck, title: "Datos cifrados", desc: "Almacenamiento sobre AWS + Supabase con backups automáticos." },
  { icon: Clock, title: "Notificaciones a tiempo", desc: "Nunca te sorprendas por un vencimiento de RCA o compromiso." },
  { icon: Lightning, title: "Rápido de adoptar", desc: "Migración desde Excel guiada. Onboarding en menos de una semana." },
  { icon: Sparkle, title: "Diseñado para directorio", desc: "Dashboards ejecutivos, reportes A4 imprimibles y presentaciones." },
];

const PAINS = [
  "Excel disperso entre equipos, versiones y correos.",
  "Compromisos RCA que se vencen sin aviso y arriesgan multas.",
  "Reuniones sin acuerdos trazables ni responsables asignados.",
  "Sin visibilidad ejecutiva sobre inversión social ni beneficiarios.",
  "Rotación de personas = pérdida de conocimiento territorial.",
];

const AUDIENCES = [
  {
    icon: Mountains,
    tag: "Empresas mineras & de energía",
    title: "Licencia social auditable",
    desc: "Cumple con SEIA, participaciones ciudadanas, ISO 26000 e IFC PS. Traza cada acuerdo, cada monto invertido, cada compromiso RCA.",
    bullets: [
      "Cumplimiento RCA en tiempo real",
      "Inversión social por eje y por comunidad",
      "Reportes para directorio y casa matriz",
      "Trazabilidad de acuerdos con dirigentes",
    ],
  },
  {
    icon: Buildings,
    tag: "Municipalidades",
    title: "Gestión territorial ordenada",
    desc: "Ordena la relación con juntas de vecinos, organizaciones sociales, autoridades regionales y empresas del territorio en un solo sistema.",
    bullets: [
      "Registro de reuniones con vecinos",
      "Seguimiento de compromisos con la ciudadanía",
      "Coordinación con SEREMIS y GORE",
      "Reportes al Concejo Municipal",
    ],
  },
  {
    icon: Compass,
    tag: "Gobiernos Regionales & Gore",
    title: "Coordinación multi-actor",
    desc: "Trazabilidad de acuerdos entre gobernador, alcaldes, empresas y comunidades. Un mismo lenguaje para todos los grupos de interés del territorio.",
    bullets: [
      "Cartera regional de proyectos e inversión",
      "Coordinación intersectorial",
      "Reportabilidad a autoridades centrales",
      "Metas del Plan de Desarrollo Regional",
    ],
  },
];

const AREAS = [
  {
    icon: Handshake, name: "Asuntos Públicos",
    points: ["Mapeo de stakeholders", "Seguimiento a parlamentarios y ministerios", "Coordinación con casa matriz"],
  },
  {
    icon: Users, name: "Comunidades",
    points: ["Fichas por comunidad y dirigente", "Historial de acuerdos", "Semáforo de riesgo relacional"],
  },
  {
    icon: Leaf, name: "Sustentabilidad / SSMA",
    points: ["Compromisos RCA con evidencia", "Reportes GRI e IFC", "Beneficiarios directos e indirectos"],
  },
  {
    icon: Scales, name: "Legal & Cumplimiento",
    points: ["Documentos por compromiso", "Bitácora de acciones", "Alertas antes del vencimiento"],
  },
  {
    icon: Briefcase, name: "Gerencia general / Directorio",
    points: ["Dashboards ejecutivos", "Reportes A4 imprimibles", "Vista consolidada por división"],
  },
];

const HOW_IT_WORKS = [
  { step: "01", title: "Onboarding guiado", desc: "Migramos tu Excel actual a SIGSAOP. Comunidades, autoridades y compromisos, cargados por nuestro equipo." },
  { step: "02", title: "Configuración de tu tenant", desc: "Tu organización con tu logo, tu equipo, roles finos y flujos aprobación adaptados a tu operación." },
  { step: "03", title: "Uso diario del equipo", desc: "Reuniones, acuerdos, evidencia y avance se registran donde ocurren. Notificaciones automáticas al responsable." },
  { step: "04", title: "Reporte a directorio", desc: "Un click a PDF, Excel o dashboard listo para tu comité, casa matriz o Concejo Municipal." },
];

export default function Landing() {
  const { user } = useAuth();
  const [demoOpen, setDemoOpen] = useState(false);
  const cta = user
    ? { label: "Ir al dashboard", to: user.role === "super_admin" ? "/admin/clientes" : "/dashboard" }
    : { label: "Ingresar", to: "/login" };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* ---------- Sticky Header ---------- */}
      <header
        data-testid="landing-header"
        className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur-md"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-16">
          <a href="#top" className="flex items-center gap-2">
            <img src="/assets/sigsaop-logo.svg?v=5" alt="SIGSAOP" className="h-9 w-auto" />
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-700">
            <a href="#problema" className="hover:text-brand-orange-600">Problema</a>
            <a href="#solucion" className="hover:text-brand-orange-600">Solución</a>
            <a href="#modulos" className="hover:text-brand-orange-600">Módulos</a>
            <a href="#para-quien" className="hover:text-brand-orange-600">Para quién</a>
            <a href="#contacto" className="hover:text-brand-orange-600">Contacto</a>
          </nav>
          <div className="flex items-center gap-2">
            <a
              href="#contacto"
              className="hidden sm:inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              data-testid="landing-demo-btn"
              onClick={(e) => { e.preventDefault(); setDemoOpen(true); }}
            >
              Solicitar demo
            </a>
            <Link
              to={cta.to}
              data-testid="landing-login-btn"
              className="inline-flex items-center gap-1 rounded-md bg-brand-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-orange-600"
            >
              {cta.label} <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* ---------- Hero ---------- */}
      <section id="top" className="relative overflow-hidden bg-slate-950 text-white">
        {/* subtle grid backdrop */}
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.35) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse at center, black 40%, transparent 80%)",
          }}
        />
        {/* glow */}
        <div
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[70rem] h-[40rem] rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(closest-side, #ff751f 0%, transparent 70%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-orange-300 mb-6 rounded-full border border-brand-orange-400/30 bg-brand-orange-500/10 px-3 py-1.5">
              <Mountains size={14} weight="fill" /> Plataforma corporativa
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              La gestión con <span className="text-brand-orange-400">comunidades, autoridades y compromisos</span>,
              <br className="hidden lg:block" />
              <span className="text-brand-green-400">ordenada, trazable y auditable.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-slate-300 text-lg leading-relaxed">
              <strong className="text-white">SIGSAOP</strong> reemplaza el Excel disperso por una plataforma corporativa donde
              tu equipo registra, controla y reporta cada relación con el territorio: acuerdos, RCA, inversión social,
              autoridades, reuniones y proyectos. Todo con trazabilidad completa, notificaciones automáticas y reportería
              lista para directorio.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to={cta.to}
                data-testid="hero-login-btn"
                className="inline-flex items-center gap-2 rounded-md bg-brand-orange-500 hover:bg-brand-orange-600 text-white font-semibold px-6 py-3 text-base shadow-lg shadow-brand-orange-500/30"
              >
                {cta.label} <ArrowRight size={16} weight="bold" />
              </Link>
              <a
                href="#contacto"
                data-testid="hero-demo-btn"
                onClick={(e) => { e.preventDefault(); setDemoOpen(true); }}
                className="inline-flex items-center gap-2 rounded-md border border-white/25 hover:border-white/60 text-white font-medium px-6 py-3 text-base cursor-pointer"
              >
                Solicitar demo
              </a>
            </div>
          </div>

          {/* Hero visual: infographic dashboard mock */}
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-6 rounded-3xl bg-gradient-to-tr from-brand-orange-500/25 to-brand-green-500/20 blur-2xl" />
            <div className="relative rounded-2xl border border-white/10 bg-slate-900/80 backdrop-blur-xl p-5 shadow-2xl">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-3 flex items-center gap-2">
                <ChartLine size={12} /> Dashboard ejecutivo
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { l: "Comunidades", v: "8", c: "orange" },
                  { l: "Autoridades", v: "155", c: "green" },
                  { l: "Compromisos", v: "14", c: "orange" },
                ].map((k) => (
                  <div key={k.l} className="rounded-lg bg-slate-800/70 border border-white/5 p-3">
                    <div className="text-[9px] uppercase tracking-wider text-slate-400">{k.l}</div>
                    <div className={`font-display text-3xl font-bold mt-1 ${k.c === "orange" ? "text-brand-orange-400" : "text-brand-green-400"}`}>
                      {k.v}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-lg bg-slate-800/70 border border-white/5 p-3">
                <div className="text-[9px] uppercase tracking-wider text-slate-400">Inversión social ejecutada</div>
                <div className="mt-2 flex items-end gap-1 h-16">
                  {[45, 60, 40, 75, 90, 65, 80, 50, 70].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-t bg-gradient-to-t from-brand-orange-500/40 to-brand-orange-400"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
                <div className="mt-2 flex justify-between text-[9px] text-slate-500">
                  <span>Ene</span><span>Sep</span>
                </div>
              </div>
              <div className="mt-3 space-y-2">
                {[
                  { l: "Compromiso RCA 038/2024 — Junta El Roble", s: "En plazo" },
                  { l: "Reunión ampliada — Comuna Sierra Gorda", s: "Programada" },
                  { l: "Proyecto Aula tecnológica — 340 alumnos", s: "Ejecutando" },
                ].map((r) => (
                  <div key={r.l} className="flex items-center justify-between text-[11px] rounded-md bg-slate-800/60 px-3 py-2 border border-white/5">
                    <span className="text-slate-200 truncate">{r.l}</span>
                    <span className="text-brand-green-400 text-[10px] uppercase tracking-wider">{r.s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* logo strip */}
      </section>

      {/* ---------- Problem ---------- */}
      <section id="problema" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-orange-600 mb-3">
              El problema
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold leading-tight tracking-tight">
              Gestionar la relación con el territorio en Excel es un riesgo silencioso.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              La información sobre comunidades, compromisos RCA, autoridades y proyectos vive dispersa entre planillas,
              correos y carpetas locales. Cuando alguien deja el equipo, se pierde. Cuando llega una auditoría, cuesta
              semanas responder. Y cuando falla, aparecen multas, conflictos y pérdida de reputación.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PAINS.map((p, i) => (
              <div
                key={i}
                className="rounded-lg border border-slate-200 bg-slate-50 p-5 flex gap-3 items-start"
              >
                <XCircle size={22} weight="fill" className="text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-slate-700 text-sm leading-relaxed">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Solution / How it works ---------- */}
      <section id="solucion" className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-green-700 mb-3">
              La solución
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
              SIGSAOP en 4 pasos: del Excel al gobierno corporativo del territorio.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              No es un CRM adaptado. Es una plataforma diseñada específicamente para las áreas que gestionan la
              licencia social: asuntos públicos, relaciones comunitarias, sustentabilidad, cumplimiento y gerencia.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.step} className="relative rounded-xl border border-slate-200 bg-white p-6">
                <div className="font-display text-5xl font-bold text-brand-orange-500/20 leading-none absolute right-4 top-4 select-none">
                  {s.step}
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-3">
                  Paso {s.step}
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Modules ---------- */}
      <section id="modulos" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col lg:flex-row items-start justify-between gap-8 mb-14">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.22em] text-brand-orange-600 mb-3">
                Módulos
              </div>
              <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
                Diez módulos que cubren toda la gestión territorial.
              </h2>
            </div>
            <p className="text-slate-600 leading-relaxed max-w-md text-sm">
              Cada módulo conversa con el siguiente. Una comunidad enlaza a sus autoridades, autoridades a compromisos,
              compromisos a proyectos y proyectos a reuniones, evidencia y presupuesto ejecutado.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {MODULES.map((m) => (
              <div
                key={m.title}
                className="group rounded-xl border border-slate-200 hover:border-brand-orange-400 hover:shadow-lg hover:-translate-y-0.5 transition p-5 bg-white"
              >
                <div className="w-10 h-10 rounded-lg bg-brand-orange-50 text-brand-orange-600 flex items-center justify-center mb-3 group-hover:bg-brand-orange-500 group-hover:text-white transition">
                  <m.icon size={20} weight="duotone" />
                </div>
                <div className="font-display font-bold text-slate-900">{m.title}</div>
                <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Value proposition ---------- */}
      <section className="py-24 bg-slate-950 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.35) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-orange-400 mb-3">
              Propuesta de valor
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
              Por qué las áreas de <span className="text-brand-orange-400">Asuntos Públicos</span> y{" "}
              <span className="text-brand-green-400">Sustentabilidad</span> eligen SIGSAOP.
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {VALUES.map((v) => (
              <div
                key={v.title}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-6 hover:bg-white/[0.06] transition"
              >
                <v.icon size={26} weight="duotone" className="text-brand-orange-400" />
                <div className="mt-4 font-display font-bold text-lg">{v.title}</div>
                <div className="mt-1.5 text-sm text-slate-400 leading-relaxed">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Audiences ---------- */}
      <section id="para-quien" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-green-700 mb-3">
              Para quién
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
              Una plataforma. Tres formas de usarla.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              SIGSAOP se adapta al lenguaje y flujos de cada tipo de organización. Multi-tenant real: cada cliente tiene
              su propia base de datos, su equipo, su logo, sus dashboards.
            </p>
          </div>

          <div className="mt-14 grid lg:grid-cols-3 gap-5">
            {AUDIENCES.map((a) => (
              <div
                key={a.tag}
                className="rounded-2xl border border-slate-200 p-7 bg-gradient-to-br from-white to-slate-50 flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-5">
                  <a.icon size={22} weight="duotone" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-orange-600 mb-2">{a.tag}</div>
                <h3 className="font-display text-2xl font-bold text-slate-900 tracking-tight">{a.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a.desc}</p>
                <ul className="mt-5 space-y-2">
                  {a.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle size={16} weight="fill" className="text-brand-green-500 flex-shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Areas ---------- */}
      <section className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-orange-600 mb-3">
              Áreas beneficiadas
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
              Un sistema, cinco áreas trabajando en la misma versión de la verdad.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {AREAS.map((a) => (
              <div key={a.name} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="w-9 h-9 rounded-lg bg-brand-green-50 text-brand-green-700 flex items-center justify-center mb-3">
                  <a.icon size={18} weight="duotone" />
                </div>
                <div className="font-display font-bold text-slate-900">{a.name}</div>
                <ul className="mt-3 space-y-1.5">
                  {a.points.map((p) => (
                    <li key={p} className="text-xs text-slate-600 leading-relaxed flex gap-1.5">
                      <span className="text-brand-orange-500 mt-0.5">›</span> {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section id="contacto" className="relative overflow-hidden bg-gradient-to-br from-brand-orange-500 to-brand-orange-600 text-white">
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.35) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-24 text-center">
          <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight leading-tight">
            Ordena tu gestión territorial hoy.
            <br />
            <span className="text-slate-950">Habla con nosotros.</span>
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to={cta.to}
              data-testid="cta-login-btn"
              className="inline-flex items-center gap-2 rounded-md bg-slate-950 hover:bg-slate-900 text-white font-semibold px-6 py-3 text-base shadow-lg"
            >
              {cta.label} <ArrowRight size={16} weight="bold" />
            </Link>
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              data-testid="cta-demo-btn"
              className="inline-flex items-center gap-2 rounded-md bg-white text-brand-orange-700 hover:bg-slate-100 font-semibold px-6 py-3 text-base shadow-lg"
            >
              Solicitar demo <ArrowRight size={16} weight="bold" />
            </button>
          </div>
          <div className="mt-10 grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto text-sm">
            <div className="rounded-lg bg-white/10 border border-white/20 px-4 py-3 backdrop-blur">
              <div className="text-[10px] uppercase tracking-widest text-white/70">Escríbenos</div>
              <div className="mt-1 font-medium">contacto@dosoft.cl</div>
            </div>
            <div className="rounded-lg bg-white/10 border border-white/20 px-4 py-3 backdrop-blur">
              <div className="text-[10px] uppercase tracking-widest text-white/70">Web</div>
              <div className="mt-1 font-medium">sigsaop.cl</div>
            </div>
            <div className="rounded-lg bg-white/10 border border-white/20 px-4 py-3 backdrop-blur">
              <div className="text-[10px] uppercase tracking-widest text-white/70">Sede</div>
              <div className="mt-1 font-medium">Antofagasta · Chile</div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="/assets/sigsaop-logo-light.svg?v=5" alt="SIGSAOP" className="h-9 w-auto" />
            <div className="text-xs">
              <div className="text-slate-200 font-medium">Sistema de Gestión Social y Ambiental</div>
              <div>© {new Date().getFullYear()} SIGSAOP. Todos los derechos reservados.</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <a href="#problema" className="hover:text-white">Problema</a>
            <a href="#modulos" className="hover:text-white">Módulos</a>
            <a href="#para-quien" className="hover:text-white">Para quién</a>
            <a href="#contacto" className="hover:text-white">Contacto</a>
            <span className="text-slate-700">|</span>
            <Link to="/terminos" className="hover:text-white" data-testid="footer-terms">Términos</Link>
            <Link to="/privacidad" className="hover:text-white" data-testid="footer-privacy">Privacidad</Link>
            <Link to="/cookies" className="hover:text-white" data-testid="footer-cookies">Cookies</Link>
            <span className="text-slate-700">|</span>
            <Link to={cta.to} className="text-brand-orange-400 hover:text-brand-orange-300 font-semibold" data-testid="footer-login-btn">
              {cta.label}
            </Link>
          </div>
        </div>
      </footer>

      <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
